import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/* ----------------------------------------
   Helpers
---------------------------------------- */

const mergeByKey = (oldList, newList, key = null) => {
    if (!Array.isArray(oldList)) oldList = [];
    if (!Array.isArray(newList)) return oldList;
    if (!key) return [...oldList, ...newList];

    const map = new Map();
    [...oldList, ...newList].forEach((item) => {
        if (item?.[key] !== undefined) {
            map.set(item[key], item);
        }
    });

    return Array.from(map.values());
};

/* ----------------------------------------
   Protected Action Keys
---------------------------------------- */

const ACTION_KEYS = new Set([
    "getStates",
    "setStates",
    "getState",
    "setState",
    "setStateSmart",
    "deleteState",
]);

/* ----------------------------------------
    Session Store (sessionStorage)
---------------------------------------- */

export const useSessionStore = create(
    persist(
        (set, get) => ({

            /* ---------- Global States ---------- */
            getStates: () => {
                const snapshot = get();
                return Object.keys(snapshot).reduce((acc, key) => {
                    if (!ACTION_KEYS.has(key)) {
                        acc[key] = snapshot[key];
                    }
                    return acc;
                }, {});
            },

            setStates: (payload = {}) =>
                set((s) => {
                    const filtered = Object.keys(payload).reduce((acc, key) => {
                        if (!ACTION_KEYS.has(key)) {
                            acc[key] = payload[key];
                        }
                        return acc;
                    }, {});
                    const keys = Object.keys(filtered);
                    if (!keys.length) return s;
                    const hasChanges = keys.some((key) => !Object.is(s[key], filtered[key]));
                    if (!hasChanges) return s;
                    return { ...s, ...filtered };
                }),

            getState: (key) => get()[key],

            setState: (key, value) =>
                set((s) => {
                    if (ACTION_KEYS.has(key)) return s;
                    if (Object.is(s[key], value)) return s;
                    return { ...s, [key]: value };
                }),

            setStateSmart: (key, value, uniqueKey = null) =>
                set((s) => {
                    if (ACTION_KEYS.has(key)) return s;
                    const merged = mergeByKey(s[key], value, uniqueKey);
                    if (Object.is(merged, s[key])) return s;
                    return {
                        ...s,
                        [key]: merged,
                    };
                }),

            deleteState: (key) =>
                set((s) => {
                    if (ACTION_KEYS.has(key)) return s;
                    const { [key]: _, ...rest } = s;
                    return rest;
                }),
        }),
        {
            name: "session-store",
            storage: createJSONStorage(() => sessionStorage),
            partialize: (state) =>
                Object.keys(state).reduce((acc, key) => {
                    if (!ACTION_KEYS.has(key)) {
                        acc[key] = state[key];
                    }
                    return acc;
                }, {}),
        }
    )
);

export default useSessionStore;
